<script setup lang="ts">
import type { Agent, Team } from '@/types/types'
import { useTransfer } from '@/composables/useTransfer'

const emit = defineEmits<{
  transfer: [agent: Agent | null, team: Team | null]
}>()

const {
  agents,
  agents_meta,
  agents_status,
  agents_search,
  teams,
  team_id,
  transfer_type,
  getAgents,
} = useTransfer()

// Bootstrap 3 .form-control (App-style square corners), .table cell/row, and
// .pager link metrics, as Tailwind utilities (10px root).
// Shared form-control metrics minus width (callers set their own width to
// avoid a w-full conflict with the fixed-width selects/search).
const formControlBase =
  'block h-[34px] px-[12px] py-[6px] text-[14px] leading-[1.42857143] text-[#555] ' +
  'bg-white border border-[#ccc] rounded-none shadow-[inset_0_1px_1px_rgba(0,0,0,0.075)] ' +
  'transition-[border-color,box-shadow] duration-150 ease-in-out focus:border-[#66afe9] ' +
  'focus:outline-none focus:shadow-[inset_0_1px_1px_rgba(0,0,0,0.075),0_0_8px_rgba(102,175,233,0.6)]'
// The three filter selects carry inline width:125px, so w-full is harmless;
// keep it for parity with the original .form-control.
const formControl = 'w-full ' + formControlBase
const td = 'p-[8px] leading-[1.42857143] align-top border-t border-[#ddd]'
const tr = 'odd:bg-[#f9f9f9] hover:bg-[#f5f5f5]'
const pagerLink = 'inline-block px-[14px] py-[5px] bg-white border border-[#ddd] hover:no-underline'
</script>

<template>
  <div class="pt-[15px] pl-0 pr-0 h-[430px] -mt-[30px]">
    <div class="text-center font-bold">
      Transfer to
      <select
        v-model="transfer_type"
        class="mb-[10px] ml-[5px] w-[125px]! inline-block!"
        :class="formControl"
      >
        <option value="user">User</option>
        <option value="team">Team</option>
      </select>
    </div>

    <select
      v-if="transfer_type == 'user'"
      v-model="agents_status"
      class="mb-[10px] ml-[5px] w-[125px]! inline-block!"
      :class="formControl"
      @change="getAgents()"
    >
      <option value="all">All Statuses</option>
      <option value="online">Available</option>
      <option value="oncall">On Call</option>
    </select>

    <select
      v-if="transfer_type == 'user'"
      v-model="team_id"
      class="mb-[10px] ml-[5px] w-[125px]! inline-block!"
      :class="formControl"
      @change="getAgents()"
    >
      <option :value="null">All Teams</option>
      <option v-for="option in teams" :key="option.id" :value="option.id">
        {{ option.name }}
      </option>
    </select>

    <input
      v-if="transfer_type == 'user'"
      v-model="agents_search"
      type="text"
      :class="formControlBase"
      class="float-right w-[180px] mb-[10px] mr-[5px] placeholder:text-[#999] placeholder:opacity-100 max-[449px]:float-none max-[449px]:w-[calc(100%-10px)] max-[449px]:ml-[5px]"
      placeholder="Search"
      @keyup="getAgents()"
    />

    <table v-if="transfer_type == 'user'" class="w-full max-w-full border-collapse mb-0 bg-white">
      <tbody>
        <tr v-if="!agents.length" :class="tr">
          <td colspan="3" class="bg-white border-none" :class="td">
            <p class="text-center">No agents found!</p>
          </td>
        </tr>
        <tr v-for="agent in agents" :key="agent.id" :class="tr">
          <td class="text-left pl-[10px] w-[30px]" :class="td">
            <i
              v-if="agent.on_call_id"
              title="On A Call"
              class="material-icons text-[#f0ad4e] text-[21px]! block!"
              >do_not_disturb_on</i
            >
            <i
              v-else-if="agent.is_available && (!agent.is_app_only || agent.is_online_app)"
              title="Available"
              class="material-icons text-[green] text-[21px]! block!"
              >check_circle</i
            >
            <i v-else title="Offline" class="material-icons text-[#d9534f] text-[21px]! block!"
              >cancel</i
            >
          </td>
          <td class="whitespace-nowrap overflow-hidden text-ellipsis max-w-[150px]" :class="td">
            {{ agent.full_name }}
          </td>
          <td class="text-right pr-[10px]" :class="td">
            <i
              v-if="
                !agent.on_call_id &&
                agent.is_available &&
                (!agent.is_app_only || agent.is_online_app)
              "
              class="material-icons float-right text-[22px]! text-[#5cb85c] cursor-pointer"
              @click="emit('transfer', agent, null)"
              >call</i
            >
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="transfer_type == 'team'" class="h-[350px] overflow-y-scroll">
      <table class="w-full max-w-full border-collapse mb-0 bg-white">
        <tbody>
          <tr v-if="!teams.length" :class="tr">
            <td colspan="3" class="bg-white border-none" :class="td">
              <p class="text-center">No teams found!</p>
            </td>
          </tr>
          <tr v-for="team in teams" :key="team.id" :class="tr">
            <td class="text-left pl-[10px] w-[30px]" :class="td">
              <i
                v-if="team.is_available"
                title="Available"
                class="material-icons text-[green] text-[21px]! block!"
                >check_circle</i
              >
              <i v-else title="Offline" class="material-icons text-[#d9534f] text-[21px]! block!"
                >cancel</i
              >
            </td>
            <td class="whitespace-nowrap overflow-hidden text-ellipsis max-w-[150px]" :class="td">
              {{ team.name }}
            </td>
            <td class="text-right pr-[10px]" :class="td">
              <i
                v-if="team.is_available"
                class="material-icons float-right text-[22px]! text-[#5cb85c] cursor-pointer"
                @click="emit('transfer', null, team)"
                >call</i
              >
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <ul
      v-if="transfer_type == 'user'"
      class="my-0 text-center list-none flow-root px-[20px] absolute right-0 left-0 bottom-[calc(5px+env(safe-area-inset-bottom,0px))]"
    >
      <li class="inline">
        <a
          :class="[
            pagerLink,
            'float-left',
            agents_meta.current_page < 2
              ? 'text-[#777] cursor-not-allowed pointer-events-none'
              : 'hover:bg-[#eee] focus:bg-[#eee]',
          ]"
          class="rounded-[4px]"
          href="#"
          rel="previous"
          @click.prevent="getAgents(agents_meta.current_page - 1)"
          >Previous</a
        >
      </li>
      <li class="inline">
        <a
          :class="[
            pagerLink,
            'float-right',
            agents_meta.current_page == agents_meta.last_page
              ? 'text-[#777] cursor-not-allowed pointer-events-none'
              : 'hover:bg-[#eee] focus:bg-[#eee]',
          ]"
          class="rounded-[4px]"
          href="#"
          rel="next"
          @click.prevent="getAgents(agents_meta.current_page + 1)"
          >Next</a
        >
      </li>
    </ul>
  </div>
</template>
